// src/pages/LoginPage.tsx - FIXED: Supabase-matching password policy with specific error messages
import React, { useState, useEffect } from 'react';
import { Formik, Form, Field, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

interface LoginValues {
  email: string;
  password: string;
}

interface RegisterValues {
  email: string;
  password: string;
  confirmPassword: string;
}

const loginSchema = Yup.object().shape({
  email: Yup.string().email('Invalid email address').required('Email is required'),
  password: Yup.string().min(6, 'Password must be at least 6 characters').required('Password is required'),
});
// eslint-disable-next-line
const registerSchema = Yup.object().shape({
  email: Yup.string().email('Invalid email address').required('Email is required'),
  password: Yup.string()
    .min(8, 'Password must be at least 8 characters')
    .matches(/[a-z]/, 'Must contain at least one lowercase letter (a–z)')
    .matches(/[A-Z]/, 'Must contain at least one uppercase letter (A–Z)')
    .matches(/[0-9]/, 'Must contain at least one number (0–9)')
    .matches( // eslint-disable-next-line
      /[!@#$%^&*()_+\-=\[\]{};':"\\|<>?,./`~]/,
      'Must contain at least one special character (!@#$%^&*...)'
    )
    .required('Password is required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password')], 'Passwords must match')
    .required('Please confirm your password'),
});

const loginInitialValues: LoginValues = { email: '', password: '' };
const registerInitialValues: RegisterValues = { email: '', password: '', confirmPassword: '' };

export default function LoginPage() {
  const { user, isLoading, needsProfileSetup, login, loginWithGoogle, register } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    console.log('🔍 LoginPage - Auth State Check:', { isLoading, hasUser: !!user, needsProfileSetup });
    if (isLoading) return;
    if (user) {
      if (needsProfileSetup) {
        console.log('🔄 New user detected, redirecting to profile setup');
        navigate('/profile-setup', { replace: true });
      } else {
        console.log('✅ Existing user, redirecting to home');
        navigate('/home', { replace: true });
      }
    }
  }, [user, isLoading, needsProfileSetup, navigate]);

  const handleLoginSubmit = async (
    values: LoginValues,
    { setSubmitting }: FormikHelpers<LoginValues>
  ) => {
    try {
      console.log('🔑 Login attempt:', values.email);
      await login(values.email, values.password);
    } catch (err: any) {
      console.error('❌ Login error:', err);
    } finally {
      setSubmitting(false); // eslint-disable-next-line
    } 
  };

  const handleRegisterSubmit = async (
    values: RegisterValues,
    { setSubmitting }: FormikHelpers<RegisterValues>
  ) => {
    try {
      console.log('📝 Registration attempt:', values.email);
      await register(values.email, values.password);
      console.log('🔄 Registration successful, redirecting to profile setup...');
      setTimeout(() => navigate('/profile-setup', { replace: true }), 300);
    } catch (err: any) {
      console.error('❌ Register error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      await loginWithGoogle();
    } catch (err) {
      console.error('❌ Google login error:', err);
    }
  };

  if (isLoading) {
    return (
      <div style={styles.loadingScreen}>
        <div style={styles.loadingInner}>
          <div style={styles.spinnerRing} />
          <p style={styles.loadingText}>Loading Onyx...</p>
        </div>
        <style>{fontImport + spinnerKeyframes}</style>
      </div>
    );
  }

  if (user) {
    return (
      <div style={styles.loadingScreen}>
        <div style={styles.loadingInner}>
          <div style={styles.spinnerRing} />
          <p style={styles.loadingText}>
            {needsProfileSetup ? 'Setting up your profile...' : 'Welcome back! Redirecting...'}
          </p>
        </div>
        <style>{fontImport + spinnerKeyframes}</style>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.halftone} />

      <nav style={styles.nav}>
        <button style={styles.backBtn} onClick={() => navigate('/')}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M9 2L4 7L9 12" stroke="#0d1a1a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>BACK</span>
        </button>
        <span style={styles.navWordmark}>ONYX</span>
      </nav>

      <main style={styles.main}>
        <div style={styles.card}>

          <div style={styles.cardHeader}>
            <span style={styles.cornerTL} />
            <span style={styles.cornerBR} />

            <div style={styles.headerInner}>
              <div style={styles.tabRow}>
                <button
                  type="button"
                  onClick={() => setIsLogin(true)}
                  style={{ ...styles.tab, ...(isLogin ? styles.tabActive : styles.tabInactive) }}
                >
                  SIGN IN
                </button>
                <button
                  type="button"
                  onClick={() => setIsLogin(false)}
                  style={{ ...styles.tab, ...(isLogin ? styles.tabInactive : styles.tabActive) }}
                >
                  JOIN
                </button>
              </div>

              <h2 style={styles.cardTitle}>
                {isLogin ? 'Welcome back.' : 'Step into the ring.'}
              </h2>
              <p style={styles.cardSub}>
                {isLogin
                  ? 'Sign in to connect with the network.'
                  : 'Create your account — just email & password to start.'}
              </p>
            </div>
          </div>

          <div style={styles.cardBody}>

            <button
              type="button"
              onClick={handleGoogleLogin}
              style={styles.googleBtn}
              onMouseEnter={e => {
                (e.currentTarget as HTMLButtonElement).style.transform = 'translate(1px,1px)';
                (e.currentTarget as HTMLButtonElement).style.boxShadow = '1px 1px 0 #0d1a1a';
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLButtonElement).style.transform = '';
                (e.currentTarget as HTMLButtonElement).style.boxShadow = '3px 3px 0 #0d1a1a';
              }}
            >
              <img
                src="https://raw.githubusercontent.com/fireflysemantics/logo/master/Google.svg"
                alt="Google"
                style={{ width: '18px', height: '18px' }}
              />
              <span>{isLogin ? 'Continue with Google' : 'Sign up with Google'}</span>
            </button>

            <div style={styles.divider}>
              <div style={styles.dividerLine} />
              <span style={styles.dividerText}>or email</span>
              <div style={styles.dividerLine} />
            </div>

            {isLogin ? (
              <Formik<LoginValues>
                key="login"
                initialValues={loginInitialValues}
                validationSchema={loginSchema}
                onSubmit={handleLoginSubmit}
              >
                {({ errors, touched, isSubmitting }) => (
                  <Form style={styles.form}>
                    <FieldGroup
                      id="email"
                      name="email"
                      type="email"
                      label="EMAIL"
                      placeholder="you@example.com"
                      autoComplete="email"
                      error={errors.email}
                      touched={touched.email}
                    />
                    <FieldGroup
                      id="password"
                      name="password"
                      type="password"
                      label="PASSWORD"
                      placeholder="••••••••"
                      autoComplete="current-password"
                      error={errors.password}
                      touched={touched.password}
                    />
                    <SubmitButton isSubmitting={isSubmitting} label="SIGN IN TO ONYX" loadingLabel="SIGNING IN..." />
                  </Form>
                )}
              </Formik>
            ) : (
              <Formik<RegisterValues>
                key="register"
                initialValues={registerInitialValues}
                validationSchema={registerSchema}
                onSubmit={handleRegisterSubmit}
              >
                {({ errors, touched, isSubmitting, values }) => (
                  <Form style={styles.form}>
                    <FieldGroup
                      id="reg-email"
                      name="email"
                      type="email"
                      label="EMAIL"
                      placeholder="you@example.com"
                      autoComplete="email"
                      error={errors.email}
                      touched={touched.email}
                    />
                    <FieldGroup
                      id="reg-password"
                      name="password"
                      type="password"
                      label="PASSWORD"
                      placeholder="Min 8 characters"
                      autoComplete="new-password"
                      error={errors.password}
                      touched={touched.password}
                    />

                    {/* ✅ Password strength checklist — visible while typing */}
                    {values.password.length > 0 && (
                      <PasswordChecklist password={values.password} />
                    )}

                    <FieldGroup
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      label="CONFIRM PASSWORD"
                      placeholder="••••••••"
                      autoComplete="new-password"
                      error={(errors as RegisterValues).confirmPassword}
                      touched={(touched as any).confirmPassword}
                    />

                    <div style={styles.infoBanner}>
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0, marginTop: '1px' }}>
                        <circle cx="7" cy="7" r="6" stroke="#5ab8b8" strokeWidth="1.5"/>
                        <path d="M7 6V10M7 4.5V4" stroke="#5ab8b8" strokeWidth="1.5" strokeLinecap="round"/>
                      </svg>
                      <span>
                        After creating your account, you'll complete your profile with username, bio, and favourite anime.
                      </span>
                    </div>

                    <SubmitButton isSubmitting={isSubmitting} label="CREATE ACCOUNT" loadingLabel="CREATING..." />
                  </Form>
                )}
              </Formik>
            )}

            <p style={styles.terms}>
              By {isLogin ? 'signing in' : 'creating an account'}, you agree to our{' '}
              <a href="/terms" style={styles.termsLink}>Terms</a>
              {' '}and{' '}
              <a href="/privacy" style={styles.termsLink}>Privacy Policy</a>
            </p>
          </div>
        </div>
      </main>

      <style>{fontImport + spinnerKeyframes + responsiveStyles}</style>
    </div>
  );
}

// ── PASSWORD CHECKLIST COMPONENT ─────────────────────────────────

function PasswordChecklist({ password }: { password: string }) {
  const rules = [
    { label: 'At least 8 characters', valid: password.length >= 8 },
    { label: 'One lowercase letter (a–z)', valid: /[a-z]/.test(password) },
    { label: 'One uppercase letter (A–Z)', valid: /[A-Z]/.test(password) },
    { label: 'One number (0–9)', valid: /[0-9]/.test(password) },// eslint-disable-next-line
    { label: 'One special character (!@#$%...)', valid: /[!@#$%^&*()_+\-=\[\]{};':"\\|<>?,./`~]/.test(password) },
  ];

  return (
    <div style={styles.checklist}>
      {rules.map(({ label, valid }) => (
        <div key={label} style={styles.checklistItem}>
          <span style={{ ...styles.checklistIcon, ...(valid ? styles.checklistIconValid : styles.checklistIconInvalid) }}>
            {valid ? '✓' : '✗'}
          </span>
          <span style={{ ...styles.checklistLabel, ...(valid ? styles.checklistLabelValid : {}) }}>
            {label}
          </span>
        </div>
      ))}
    </div>
  );
}

// ── SUB-COMPONENTS ───────────────────────────────────────────────

function FieldGroup({
  id, name, type, label, placeholder, autoComplete, error, touched,
}: {
  id: string; name: string; type: string; label: string;
  placeholder: string; autoComplete: string;
  error?: string; touched?: boolean;
}) {
  const hasError = !!(error && touched);
  return (
    <div style={styles.fieldGroup}>
      <label htmlFor={id} style={styles.label}>{label}</label>
      <Field
        id={id}
        name={name}
        type={type}
        autoComplete={autoComplete}
        placeholder={placeholder}
        style={{
          ...styles.input,
          ...(hasError ? styles.inputError : {}),
        }}
      />
      {hasError && <span style={styles.errorMsg}>{error}</span>}
    </div>
  );
}

function SubmitButton({ isSubmitting, label, loadingLabel }: {
  isSubmitting: boolean; label: string; loadingLabel: string;
}) {
  return (
    <button
      type="submit"
      disabled={isSubmitting}
      style={{
        ...styles.submitBtn,
        ...(isSubmitting ? styles.submitBtnDisabled : {}),
      }}
      onMouseEnter={e => {
        if (!isSubmitting) {
          (e.currentTarget as HTMLButtonElement).style.transform = 'translate(2px,2px)';
          (e.currentTarget as HTMLButtonElement).style.boxShadow = '2px 2px 0 #0d1a1a, 3px 3px 0 #8a5a00';
        }
      }}
      onMouseLeave={e => {
        (e.currentTarget as HTMLButtonElement).style.transform = '';
        (e.currentTarget as HTMLButtonElement).style.boxShadow = '4px 4px 0 #0d1a1a, 6px 6px 0 #8a5a00';
      }}
    >
      {isSubmitting ? (
        <span style={styles.submitInner}>
          <span style={styles.spinnerSmall} />
          {loadingLabel}
        </span>
      ) : label}
    </button>
  );
}

// ── STYLES ───────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  loadingScreen: {
    minHeight: '100vh',
    background: '#7ecece',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingInner: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
  },
  spinnerRing: {
    width: '48px',
    height: '48px',
    border: '3px solid rgba(13,26,26,0.15)',
    borderTop: '3px solid #0d1a1a',
    borderRadius: '50%',
    animation: 'onyxSpin 0.8s linear infinite',
  },
  loadingText: {
    fontFamily: "'M PLUS Rounded 1c', sans-serif",
    fontSize: '13px',
    fontWeight: 700,
    letterSpacing: '2px',
    color: '#0d1a1a',
  },
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(180deg, #a8e6e6 0%, #7ecece 40%, #68bebe 100%)',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    fontFamily: "'M PLUS Rounded 1c', sans-serif",
  },
  halftone: {
    position: 'fixed',
    inset: 0,
    backgroundImage: 'radial-gradient(circle, rgba(13,26,26,0.06) 1px, transparent 1px)',
    backgroundSize: '22px 22px',
    pointerEvents: 'none',
    zIndex: 0,
  },
  nav: {
    position: 'relative',
    zIndex: 10,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
  },
  backBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    background: 'rgba(13,26,26,0.08)',
    border: '2px solid rgba(13,26,26,0.25)',
    borderRadius: '6px',
    padding: '6px 14px',
    fontSize: '11px',
    fontWeight: 900,
    letterSpacing: '2px',
    color: '#0d1a1a',
    cursor: 'pointer',
    fontFamily: "'M PLUS Rounded 1c', sans-serif",
    transition: 'all 0.1s',
  },
  navWordmark: {
    fontFamily: "'Bebas Neue', sans-serif",
    fontSize: '22px',
    letterSpacing: '4px',
    color: '#0d1a1a',
    opacity: 0.5,
  },
  main: {
    position: 'relative',
    zIndex: 10,
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px 16px 32px',
  },
  card: {
    width: '100%',
    maxWidth: '440px',
    background: '#f0fafa',
    border: '3px solid #0d1a1a',
    borderRadius: '4px',
    boxShadow: '6px 6px 0 #0d1a1a',
    overflow: 'hidden',
    position: 'relative',
  },
  cardHeader: {
    background: '#0d1a1a',
    padding: '28px 28px 24px',
    position: 'relative',
  },
  cornerTL: {
    position: 'absolute',
    top: 10, left: 10,
    width: 16, height: 16,
    borderTop: '2px solid rgba(126,206,206,0.3)',
    borderLeft: '2px solid rgba(126,206,206,0.3)',
    display: 'block',
  },
  cornerBR: {
    position: 'absolute',
    bottom: 10, right: 10,
    width: 16, height: 16,
    borderBottom: '2px solid rgba(126,206,206,0.3)',
    borderRight: '2px solid rgba(126,206,206,0.3)',
    display: 'block',
  },
  headerInner: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  tabRow: {
    display: 'flex',
    gap: '4px',
    background: 'rgba(126,206,206,0.08)',
    border: '1.5px solid rgba(126,206,206,0.2)',
    borderRadius: '4px',
    padding: '3px',
  },
  tab: {
    flex: 1,
    padding: '8px',
    borderRadius: '2px',
    border: 'none',
    fontSize: '11px',
    fontWeight: 900,
    letterSpacing: '2px',
    cursor: 'pointer',
    transition: 'all 0.15s',
    fontFamily: "'M PLUS Rounded 1c', sans-serif",
  },
  tabActive: {
    background: '#e8a820',
    color: '#0d1a1a',
    boxShadow: '2px 2px 0 rgba(13,26,26,0.4)',
  },
  tabInactive: {
    background: 'transparent',
    color: 'rgba(126,206,206,0.5)',
  },
  cardTitle: {
    fontFamily: "'Bebas Neue', sans-serif",
    fontSize: '32px',
    letterSpacing: '2px',
    color: '#f0fafa',
    margin: 0,
    lineHeight: 1,
  },
  cardSub: {
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '1px',
    color: 'rgba(126,206,206,0.55)',
    margin: 0,
  },
  cardBody: {
    padding: '24px 24px 20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  googleBtn: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    padding: '11px 16px',
    background: '#ffffff',
    border: '2px solid #0d1a1a',
    borderRadius: '4px',
    boxShadow: '3px 3px 0 #0d1a1a',
    fontSize: '12px',
    fontWeight: 900,
    letterSpacing: '1px',
    color: '#0d1a1a',
    cursor: 'pointer',
    fontFamily: "'M PLUS Rounded 1c', sans-serif",
    transition: 'transform 0.1s, box-shadow 0.1s',
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  dividerLine: {
    flex: 1,
    height: '1px',
    background: 'rgba(13,26,26,0.15)',
  },
  dividerText: {
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '2px',
    color: 'rgba(13,26,26,0.35)',
    textTransform: 'uppercase' as const,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
  },
  label: {
    fontSize: '10px',
    fontWeight: 900,
    letterSpacing: '2px',
    color: 'rgba(13,26,26,0.55)',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    background: '#ffffff',
    border: '2px solid rgba(13,26,26,0.25)',
    borderRadius: '4px',
    fontSize: '13px',
    fontWeight: 700,
    color: '#0d1a1a',
    fontFamily: "'M PLUS Rounded 1c', sans-serif",
    outline: 'none',
    transition: 'border-color 0.15s',
    boxSizing: 'border-box' as const,
  },
  inputError: {
    borderColor: '#c0392b',
    background: '#fff8f8',
  },
  errorMsg: {
    fontSize: '10px',
    fontWeight: 700,
    color: '#c0392b',
    letterSpacing: '0.5px',
  },

  // ✅ Password checklist styles
  checklist: {
    background: 'rgba(13,26,26,0.04)',
    border: '1.5px solid rgba(13,26,26,0.1)',
    borderRadius: '4px',
    padding: '10px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
  },
  checklistItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  checklistIcon: {
    fontSize: '11px',
    fontWeight: 900,
    width: '14px',
    textAlign: 'center' as const,
    flexShrink: 0,
  },
  checklistIconValid: {
    color: '#1d9e75',
  },
  checklistIconInvalid: {
    color: 'rgba(13,26,26,0.3)',
  },
  checklistLabel: {
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.3px',
    color: 'rgba(13,26,26,0.45)',
  },
  checklistLabelValid: {
    color: '#1d9e75',
  },

  infoBanner: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    background: 'rgba(90,184,184,0.12)',
    border: '1.5px solid rgba(90,184,184,0.4)',
    borderRadius: '4px',
    padding: '10px 12px',
    fontSize: '11px',
    fontWeight: 700,
    color: 'rgba(13,26,26,0.65)',
    lineHeight: 1.5,
    letterSpacing: '0.3px',
  },
  submitBtn: {
    width: '100%',
    padding: '13px',
    background: '#e8a820',
    border: '3px solid #0d1a1a',
    borderRadius: '4px',
    boxShadow: '4px 4px 0 #0d1a1a, 6px 6px 0 #8a5a00',
    fontSize: '14px',
    fontWeight: 900,
    letterSpacing: '2px',
    color: '#0d1a1a',
    cursor: 'pointer',
    fontFamily: "'M PLUS Rounded 1c', sans-serif",
    transition: 'transform 0.1s, box-shadow 0.1s',
    marginTop: '4px',
  },
  submitBtnDisabled: {
    opacity: 0.55,
    cursor: 'not-allowed',
    boxShadow: '2px 2px 0 #0d1a1a',
  },
  submitInner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  },
  spinnerSmall: {
    width: '14px',
    height: '14px',
    border: '2px solid rgba(13,26,26,0.2)',
    borderTop: '2px solid #0d1a1a',
    borderRadius: '50%',
    display: 'inline-block',
    animation: 'onyxSpin 0.8s linear infinite',
  },
  terms: {
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '0.5px',
    color: 'rgba(13,26,26,0.4)',
    textAlign: 'center' as const,
    lineHeight: 1.6,
  },
  termsLink: {
    color: '#3a9898',
    textDecoration: 'none',
    fontWeight: 900,
  },
};

// ── INJECTED STYLES ──────────────────────────────────────────────

const fontImport = `
  @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=M+PLUS+Rounded+1c:wght@400;700;900&display=swap');
`;

const spinnerKeyframes = `
  @keyframes onyxSpin {
    to { transform: rotate(360deg); }
  }
`;

const responsiveStyles = `
  input:focus {
    border-color: #5ab8b8 !important;
    box-shadow: 0 0 0 3px rgba(90,184,184,0.2);
  }
  a[href="/terms"]:hover,
  a[href="/privacy"]:hover {
    color: #0d1a1a;
  }
  @media (max-width: 480px) {
    main > div {
      box-shadow: 4px 4px 0 #0d1a1a;
    }
  }
  @media (min-height: 800px) {
    main {
      padding-bottom: 60px;
    }
  }
  @media (max-width: 768px) {
    input, select, textarea {
      font-size: 16px !important;
    }
  }
`;